import { I18NextPipe } from 'angular-i18next';
import {
  BackupHistoryDayInfo,
  BackupHistoryDayInfoDetail,
  BackupHistoryRemoteDayInfo,
  BackupStatuses,
  RestoreVerificationStatus
} from 'mbs-ui-shared@models/backupHistory';
import moment from 'moment';

const dangerColor = 'text-danger';
const warningColor = 'text-yellow';
const successColor = 'text-green';
const grayColor = 'text-gray-600';
const blueColor = 'text-blue';

export const backupIconMap: Map<BackupStatuses, string> = new Map([
  [BackupStatuses.Success, 'ico ico-check-circle'],
  [BackupStatuses.Running, 'ico ico-circleArrowRight'],
  [BackupStatuses.Warning, 'ico ico-error'],
  [BackupStatuses.Interrupted, 'ico ico-error'],
  [BackupStatuses.MissingRun, 'fa fa-circle-o'],
  [BackupStatuses.Failed, 'ico ico-failed'],
  [BackupStatuses.UnexpectedlyClosed, 'ico ico-failed'],
  [BackupStatuses.PlanDeleted, 'fa fa-trash'],
  [BackupStatuses.Scheduled, 'ico ico-ClockO']
]);

export const backupIconColorMap: Map<BackupStatuses, string> = new Map([
  [BackupStatuses.Success, successColor],
  [BackupStatuses.Running, successColor],
  [BackupStatuses.Warning, warningColor],
  [BackupStatuses.Interrupted, warningColor],
  [BackupStatuses.MissingRun, dangerColor],
  [BackupStatuses.Failed, dangerColor],
  [BackupStatuses.UnexpectedlyClosed, dangerColor],
  [BackupStatuses.PlanDeleted, grayColor],
  [BackupStatuses.Scheduled, blueColor]
]);

export const backupTextMap: Map<BackupStatuses, string> = new Map([
  [BackupStatuses.Unknown, ''],
  [BackupStatuses.Success, 'backup-history:backupStatus.success'],
  [BackupStatuses.Failed, 'backup-history:backupStatus.failed'],
  [BackupStatuses.Interrupted, 'backup-history:backupStatus.interrupted'],
  [BackupStatuses.UnexpectedlyClosed, 'backup-history:backupStatus.unexpectedlyClosed'],
  [BackupStatuses.Running, 'backup-history:backupStatus.running'],
  [BackupStatuses.Warning, 'backup-history:backupStatus.warning'],
  [BackupStatuses.PlanCreated, 'backup-history:backupStatus.planCreated'],
  [BackupStatuses.PlanDeleted, 'backup-history:backupStatus.planDeleted'],
  [BackupStatuses.Scheduled, 'backup-history:backupStatus.scheduled'],
  [BackupStatuses.MissingRun, 'backup-history:backupStatus.missingRun']
]);

export const verificationIconMap: Map<RestoreVerificationStatus, string> = new Map([
  [RestoreVerificationStatus.None, ''],
  [RestoreVerificationStatus.Executing, 'ico ico-circleArrowRight'],
  [RestoreVerificationStatus.Success, 'ico ico-success-shield'],
  [RestoreVerificationStatus.Warning, 'ico ico-warning-shield']
]);

export const verificationIconColorMap: Map<RestoreVerificationStatus, string> = new Map([
  [RestoreVerificationStatus.Success, successColor],
  [RestoreVerificationStatus.Warning, warningColor],
  [RestoreVerificationStatus.Executing, successColor]
]);

export const verificationTextMap: Map<RestoreVerificationStatus, string> = new Map([
  [RestoreVerificationStatus.None, ''],
  [RestoreVerificationStatus.Executing, 'backup-history:verificationStatus.executing'],
  [RestoreVerificationStatus.Warning, 'backup-history:verificationStatus.warning'],
  [RestoreVerificationStatus.Success, 'backup-history:verificationStatus.success']
]);

export const prepareLocalStatusData = (
  data: BackupHistoryRemoteDayInfo[],
  i18N: I18NextPipe,
  startDate: string,
  days: number
): BackupHistoryDayInfo => {
  const result = { worstStatus: [], bestStatus: [], lastStatus: [] } as BackupHistoryDayInfo;

  if (!startDate || startDate.length !== 8 || typeof days !== 'number' || !days) return result;

  for (let i = 0; i < days; i++) {
    const date = moment(startDate, 'YYYYMMDD').add(i, 'days');
    const remoteData = data.find((d) => d.date?.day === date.date() && d.date?.month === date.month() + 1 && d.date?.year === date.year());

    result.worstStatus.push(
      getStatusInfo(remoteData?.worstSession?.backupStatus, remoteData?.worstSession?.restoreVerificationStatus, i18N)
    );
    result.bestStatus.push(getStatusInfo(remoteData?.bestSession?.backupStatus, remoteData?.bestSession?.restoreVerificationStatus, i18N));
    result.lastStatus.push(getStatusInfo(remoteData?.lastSession?.backupStatus, remoteData?.lastSession?.restoreVerificationStatus, i18N));
  }

  return result;
};

export const getStatusInfo = (
  status: BackupStatuses,
  verificationStatus: RestoreVerificationStatus,
  i18N: I18NextPipe,
  skipTextColor = false
): BackupHistoryDayInfoDetail => {
  const result = { backupStatus: null, verificationStatus: null } as BackupHistoryDayInfoDetail;

  if (status && backupIconMap.has(status) && (skipTextColor || backupIconColorMap.has(status))) {
    result.backupStatus = {
      icon: `${backupIconMap.get(status)} ${skipTextColor ? '' : backupIconColorMap.get(status)}`,
      tooltip: getBackupStatusText(status, i18N)
    };
  }

  if (
    verificationStatus &&
    verificationIconMap.has(verificationStatus) &&
    (skipTextColor || verificationIconColorMap.has(verificationStatus))
  ) {
    result.verificationStatus = {
      icon: `${verificationIconMap.get(verificationStatus)} ${skipTextColor ? '' : verificationIconColorMap.get(verificationStatus)}`,
      tooltip: getRestoreVerificationStatusText(verificationStatus, i18N)
    };
  }

  return result;
};

export const getBackupStatusText = (status: BackupStatuses, i18N: I18NextPipe): string => {
  return backupTextMap.has(status) ? i18N.transform(backupTextMap.get(status)) : '';
};

export const getRestoreVerificationStatusText = (verificationStatus: RestoreVerificationStatus, i18N: I18NextPipe): string => {
  return verificationTextMap.has(verificationStatus) ? i18N.transform(verificationTextMap.get(verificationStatus)) : '';
};
